import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Select, FormControl, InputLabel, LinearProgress,
  IconButton, Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { addAppVersion, getAppVersions } from "../../Redux/Actions/peerAction";
import { api } from "../../api/api";

const STATUS_COLORS = {
  released: "success",
  testing: "warning",
  draft: "default",
  deprecated: "error",
};

function ApkManager({ appVersions = [], screens = [] }) {
  const dispatch = useDispatch();

  // Register dialog
  const [openRegister, setOpenRegister] = useState(false);
  const [form, setForm] = useState({
    packageName: "com.digntec.signage",
    versionCode: "",
    versionName: "",
    displayName: "DigntecSignage",
    apkPath: "",
    targetChipset: "all",
    status: "draft",
    forceUpdate: false,
  });

  // Deploy dialog
  const [openDeploy, setOpenDeploy] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [deployTargets, setDeployTargets] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [deployResults, setDeployResults] = useState([]);

  // APK 업로드 — 패키지·버전은 서버가 파일에서 읽는다. testing 으로 등록 → '배포' 를 눌러야 OTA 대상
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const notes = window.prompt('릴리스 노트 (선택)', '') ?? '';
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('apk', file);
      fd.append('releaseNotes', notes);
      const { data } = await api.post('/peer/app-versions/upload', fd);
      toast.success(`업로드 완료: ${data.version.displayName} v${data.version.versionName} (testing)`);
      dispatch(getAppVersions());
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
    setUploading(false);
  };
  // 배포 = released + forceUpdate (TV 가 OTA 로 받아 설치). 다시 누르면 testing 으로 (배포 중지 — 이미 받은 TV 는 그대로)
  const setReleased = async (v, release) => {
    const msg = release
      ? `${v.displayName || v.packageName} v${v.versionName} 을(를) 배포합니다.\n이 앱을 쓰는 모든 TV 가 다음 OTA 확인(24시간마다) 때 자동으로 설치합니다 (앱이 잠깐 다시 시작됨). 계속할까요?`
      : `v${v.versionName} 배포를 중지합니다 (새로 받는 TV 만 멈춤). 계속할까요?`;
    if (!window.confirm(msg)) return;
    try {
      // 앱은 forceUpdate=true 일 때만 실제로 설치한다 (OtaUpdateManager) — 배포 = released + forceUpdate
      await api.patch(`/peer/app-versions/${v._id}`, { status: release ? 'released' : 'testing', forceUpdate: release });
      toast.success(release ? `v${v.versionName} 배포됨` : `v${v.versionName} 배포 중지`);
      dispatch(getAppVersions());
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const handleRegister = async () => {
    const payload = {
      ...form,
      versionCode: parseInt(form.versionCode, 10),
    };
    await dispatch(addAppVersion(payload));
    dispatch(getAppVersions());
    setOpenRegister(false);
    setForm({
      packageName: "com.digntec.signage",
      versionCode: "",
      versionName: "",
      displayName: "DigntecSignage",
      apkPath: "",
      targetChipset: "all",
      status: "draft",
      forceUpdate: false,
    });
  };

  const connectedDevices = screens.filter(
    (s) => s.wgStatus === "connected" && s.wgIp && !s.mac?.startsWith("PLH_")
  );

  const openDeployDialog = (version) => {
    setSelectedVersion(version);
    setDeployTargets(connectedDevices.map((d) => d._id));
    setDeployResults([]);
    setOpenDeploy(true);
  };

  const toggleTarget = (id) => {
    setDeployTargets((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleDeploy = async () => {
    if (!selectedVersion || deployTargets.length === 0) return;
    setDeploying(true);
    setDeployResults([]);

    const results = [];
    for (const screenId of deployTargets) {
      const screen = screens.find((s) => s._id === screenId);
      if (!screen?.wgIp) {
        results.push({ screenId, name: screen?.name, status: "error", detail: "No WG IP" });
        continue;
      }
      try {
        const res = await api.post("/peer/deploy-apk", {
          screenId,
          wgIp: screen.wgIp,
          apkPath: selectedVersion.apkPath,
          packageName: selectedVersion.packageName,
          versionName: selectedVersion.versionName,
        });
        results.push({
          screenId,
          name: screen.name,
          status: res.data?.success ? "success" : "error",
          detail: res.data?.message || "Done",
        });
      } catch (err) {
        results.push({
          screenId,
          name: screen.name,
          status: "error",
          detail: err.response?.data?.message || err.message,
        });
      }
      setDeployResults([...results]);
    }
    setDeploying(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#15192C" }}>
          APK Version Management
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
        <input ref={fileRef} type="file" accept=".apk" hidden onChange={handleUpload} />
        <Button
          variant="contained"
          size="small"
          startIcon={<CloudUploadIcon />}
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          sx={{ background: "#551FFF", textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 12 }}
        >
          {uploading ? "업로드 중..." : "APK 업로드"}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpenRegister(true)}
          sx={{ color: "#551FFF", borderColor: "#551FFF", textTransform: "none", borderRadius: 2, fontWeight: 600, fontSize: 12 }}
        >
          Register Version
        </Button>
        </Box>
      </Box>

      {/* Version Table */}
      {appVersions.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "#bbb", textAlign: "center", py: 3 }}>
          No APK versions registered
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ borderBottom: "2px solid #551FFF" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Package</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Version</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Code</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Chipset</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Force</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appVersions.map((v) => (
                <TableRow key={v._id} hover>
                  <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>{v.displayName || v.packageName}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>v{v.versionName}</TableCell>
                  <TableCell sx={{ fontSize: 12 }} align="center">{v.versionCode}</TableCell>
                  <TableCell sx={{ fontSize: 12 }} align="center">{v.targetChipset || "all"}</TableCell>
                  <TableCell align="center">
                    <Chip label={v.status} size="small" color={STATUS_COLORS[v.status] || "default"} variant="outlined" sx={{ fontSize: 10 }} />
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 12 }}>
                    {v.forceUpdate ? "Yes" : "-"}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {v.status !== "deprecated" && (
                      <Button size="small" variant={v.status === "released" ? "outlined" : "contained"}
                        onClick={() => setReleased(v, v.status !== "released")}
                        sx={{ textTransform: "none", fontSize: 11, py: 0.2, mr: 0.5, ...(v.status === "released" ? {} : { background: "#16A34A" }) }}>
                        {v.status === "released" ? "배포 중지" : "배포"}
                      </Button>
                    )}
                    <Tooltip title={`Deploy v${v.versionName} to devices`}>
                      <IconButton
                        size="small"
                        onClick={() => openDeployDialog(v)}
                        disabled={v.status === "deprecated"}
                        sx={{ color: "#551FFF" }}
                      >
                        <RocketLaunchIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* ===== Register Dialog ===== */}
      <Dialog open={openRegister} onClose={() => setOpenRegister(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "3px solid #551FFF" }}>
          Register New APK Version
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Package Name"
            value={form.packageName}
            onChange={(e) => setForm({ ...form, packageName: e.target.value })}
            size="small" fullWidth
          />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField
              label="Version Name"
              placeholder="1.2.3"
              value={form.versionName}
              onChange={(e) => setForm({ ...form, versionName: e.target.value })}
              size="small" fullWidth
            />
            <TextField
              label="Version Code"
              placeholder="123"
              type="number"
              value={form.versionCode}
              onChange={(e) => setForm({ ...form, versionCode: e.target.value })}
              size="small" fullWidth
            />
          </Box>
          <TextField
            label="Display Name"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            size="small" fullWidth
          />
          <TextField
            label="APK Path (server)"
            placeholder="/uploads/apks/DigntecSignage-v1.2.3.apk"
            value={form.apkPath}
            onChange={(e) => setForm({ ...form, apkPath: e.target.value })}
            size="small" fullWidth
          />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Target Chipset</InputLabel>
              <Select
                value={form.targetChipset}
                label="Target Chipset"
                onChange={(e) => setForm({ ...form, targetChipset: e.target.value })}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="rk3519">RK3519</MenuItem>
                <MenuItem value="rk3539">RK3539</MenuItem>
                <MenuItem value="rk3518">RK3518</MenuItem>
                <MenuItem value="rk3568">RK3568</MenuItem>
                <MenuItem value="s905x3">S905X3</MenuItem>
                <MenuItem value="s905x4">S905X4</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                label="Status"
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="testing">Testing</MenuItem>
                <MenuItem value="released">Released</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenRegister(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRegister}
            disabled={!form.versionName || !form.versionCode}
            sx={{ background: "#551FFF", textTransform: "none", fontWeight: 600 }}
          >
            Register
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Deploy Dialog ===== */}
      <Dialog open={openDeploy} onClose={() => !deploying && setOpenDeploy(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: "3px solid #551FFF", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Deploy {selectedVersion?.displayName} v{selectedVersion?.versionName}</span>
          {!deploying && (
            <IconButton size="small" onClick={() => setOpenDeploy(false)}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          {deploying && <LinearProgress sx={{ mb: 1.5, "& .MuiLinearProgress-bar": { background: "#551FFF" } }} />}

          <Typography sx={{ fontSize: 12, color: "#666", mb: 1 }}>
            Select target devices ({connectedDevices.length} connected):
          </Typography>

          <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
            {connectedDevices.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#bbb", textAlign: "center", py: 2 }}>
                No WG-connected devices available
              </Typography>
            ) : (
              connectedDevices.map((d) => {
                const result = deployResults.find((r) => r.screenId === d._id);
                return (
                  <Box
                    key={d._id}
                    onClick={() => !deploying && toggleTarget(d._id)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1, p: 1, mb: 0.5,
                      borderRadius: 2, cursor: deploying ? "default" : "pointer",
                      background: deployTargets.includes(d._id) ? "rgba(85,31,255,0.06)" : "#fafafa",
                      border: deployTargets.includes(d._id) ? "1.5px solid #551FFF" : "1.5px solid transparent",
                      transition: "all 0.15s",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#15192C" }}>{d.name || "Unnamed"}</Typography>
                      <Typography sx={{ fontSize: 11, color: "#888", fontFamily: "monospace" }}>
                        {d.wgIp} · {d.mac}
                      </Typography>
                    </Box>
                    {result && (
                      result.status === "success"
                        ? <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 18 }} />
                        : <Tooltip title={result.detail}><ErrorIcon sx={{ color: "#ef4444", fontSize: 18 }} /></Tooltip>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDeploy(false)} disabled={deploying} sx={{ textTransform: "none" }}>
            {deployResults.length > 0 ? "Close" : "Cancel"}
          </Button>
          {deployResults.length === 0 && (
            <Button
              variant="contained"
              startIcon={<RocketLaunchIcon />}
              onClick={handleDeploy}
              disabled={deploying || deployTargets.length === 0}
              sx={{ background: "#551FFF", textTransform: "none", fontWeight: 600 }}
            >
              Deploy to {deployTargets.length} device{deployTargets.length !== 1 ? "s" : ""}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ApkManager;
